import { useSelector } from 'react-redux';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { IconEditFilled } from '@zillow/constellation-icons';
import { Accordion, Box, Heading, Icon, Paragraph, Text } from '@zillow/constellation-10';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { HiddenText } from '@common/components/HiddenText/HiddenText';
import TransactionConstants from '@common/constants/TransactionConstants';
import MoveInCostsItemsDisplay from '@renter-hub/components/MoveInCostsItemsInfo';
import PontoonConstants from '@common/constants/PontoonExceptionConstants';
import PaymentMethodModule from '@renter-hub/components/PaymentMethodModule';
import { MOVE_IN_COSTS_PAYMENT } from '@renter-hub/constants/paymentConstants';
import { RootState } from '../../../../../../store/types';

interface Props {
  goToEditMoveInCostsPage?: any;
  displayHiddenText?: boolean;
  goToEditPaymentInfoPage: (typeOfPaymentToBeUpdated: typeof MOVE_IN_COSTS_PAYMENT) => void;
}

const MoveInCostsDetails = ({ displayHiddenText, goToEditMoveInCostsPage, goToEditPaymentInfoPage }: Props) => {
  const moveInCostsDetails = useSelector((state: RootState) => state.payments.renter.dashboard.moveInCostsDetails);
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);

  const { dueDate, sendDate, status, paymentMethodId, isScheduled, baseAmount, groupKey } = moveInCostsDetails;
  const renterCanEditMoveInCosts =
    status === TransactionConstants.STATUS.UNPAID || status === TransactionConstants.STATUS.REFUNDED;
  const selectedPaymentMethod = paymentMethods.find((paymentMethod) => paymentMethod.id === paymentMethodId);

  const getMoveInCostsData = () => {
    if (isScheduled) {
      const feeDetails = selectedPaymentMethod?.oneTimePaymentAmountDetails.find(
        (moveInCostDetails) => moveInCostDetails.groupKey === groupKey,
      );
      const fee = feeDetails?.fee || 0;
      const total = feeDetails?.totalAmount || 0;
      const feeText = fee ? ` (${centsToDollars(fee)} fee)` : '';
      const displayText = total ? `${centsToDollars(total)}${feeText}` : '-';

      return {
        displayText,
        fee,
        calculatedTotalAmount: total,
      };
    } else {
      const total = baseAmount || 0;
      const displayText = total ? centsToDollars(total) : <HiddenText visible={displayHiddenText} />;

      return {
        displayText,
        fee: 0,
        calculatedTotalAmount: total,
      };
    }
  };

  const { displayText, fee, calculatedTotalAmount } = getMoveInCostsData();

  return (
    <>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 'token(spacing.sm)',
        }}
      >
        <Box
          css={{
            paddingRight: 'token(spacing.sm) !important',
            paddingTop: 'token(spacing.sm) !important',
            paddingBottom: 'token(spacing.sm) !important',
          }}
        >
          <Paragraph textStyle="body-bold">Due date</Paragraph>
          <Text>
            {dueDate ? (
              `${formatDate(
                dueDate,
                'MMMM dd, yyyy',
                PontoonConstants.RENTER_HUB.RENTER_DASHBOARD.MOVE_IN_COSTS.DUE_DATE,
              )}`
            ) : (
              <HiddenText visible={displayHiddenText} />
            )}
          </Text>
        </Box>
        {renterCanEditMoveInCosts && sendDate && (
          <Box
            css={{
              paddingRight: 'token(spacing.sm) !important',
              paddingTop: 'token(spacing.sm) !important',
              paddingBottom: 'token(spacing.sm) !important',
            }}
          >
            <Paragraph textStyle="body-bold">
              Send date
              {renterCanEditMoveInCosts && (
                <Icon
                  size="sm"
                  css={{ color: 'brand', marginLeft: 'token(spacing.xs) !important' }}
                  render={<IconEditFilled />}
                  onClick={goToEditMoveInCostsPage}
                />
              )}
            </Paragraph>
            <Text>
              {sendDate ? `${formatDate(sendDate, 'MMMM dd, yyyy')}` : <HiddenText visible={displayHiddenText} />}
            </Text>
          </Box>
        )}
        {selectedPaymentMethod && (
          <Box
            css={{
              paddingRight: 'token(spacing.sm) !important',
              paddingTop: 'token(spacing.sm) !important',
              paddingBottom: 'token(spacing.sm) !important',
            }}
          >
            <PaymentMethodModule
              accountName={selectedPaymentMethod.displayName || selectedPaymentMethod.nickname}
              accountLast4={selectedPaymentMethod.accountLast4}
              onClick={() => {
                goToEditPaymentInfoPage(MOVE_IN_COSTS_PAYMENT);
              }}
              editable
            />
          </Box>
        )}
        <Box
          css={{
            paddingRight: 'token(spacing.sm) !important',
            paddingTop: 'token(spacing.sm) !important',
            paddingBottom: 'token(spacing.sm) !important',
          }}
        >
          <Paragraph textStyle="body-bold">{!isScheduled ? 'Amount' : 'Total payment'}</Paragraph>
          <Text>{displayText}</Text>
        </Box>
      </Box>
      {moveInCostsDetails?.items.length > 0 ? (
        <Box css={{ marginTop: 'token(spacing.md) !important' }}>
          <Accordion.Root>
            <Accordion.Item value="payment-includes-accordion">
              <Accordion.Header css={{ padding: '12px 16px !important' }}>
                <Heading level={6}>What&apos;s included</Heading>
              </Accordion.Header>
              <Accordion.Panel css={{ padding: '12px 16px !important' }}>
                <MoveInCostsItemsDisplay
                  zgFee={fee}
                  totalAmount={calculatedTotalAmount}
                  items={moveInCostsDetails.items}
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion.Root>
        </Box>
      ) : (
        <HiddenText visible={displayHiddenText} />
      )}
    </>
  );
};

export default MoveInCostsDetails;
